import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import logo from "images/logo.png";
import "modules/authentication/login/login.scss";
import {
  Box,
  Card,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  FormControl,
  InputAdornment,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core";
import { getHomePage } from "modules/platform-components/utils/configuration";
import {
  apiPaths,
  getAdminData,
  postData,
} from "modules/platform-components/api-integration-util";

import { forgetSchema } from "./helper";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    width: "100%",
    height: 50,
    textTransform: "capitalize",
  },
  back: {
    color: "#3e4095",
    fontSize: 14,
    cursor: "pointer",
  },
  errorMessage: {
    width: "100%",
    "& > p": {
      color: "red",
    },
  },
}));

const Login = () => {
  const classes = useStyles();
  const history = useHistory();
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenantIds, setTenantIds] = useState([]);

  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, errors }: any = useForm({
    validationSchema: forgetSchema,
  });

  const fetchTenantData = useCallback(async () => {
    try {
      const response = await getAdminData(apiPaths.getTenants);
      setTenantIds(response.result);
      if (response.result.length > 0) {
        setSelectedTenantId(response.result[0].tenantId);
      }
    } catch (error) {
      console.error("Error fetching QB data:", error);
    }
  }, []);

  useEffect(() => {
    fetchTenantData();
  }, [fetchTenantData]);

  const handlePasswordVisibility = () => setShowPassword(!showPassword);

  const onSubmit = async (data) => {
    try {
      postData(apiPaths.login, {
        data,
        headers: { Tenantid: selectedTenantId },
      })
        .then((val) => {
          if (val?.responseCode === 200 && val?.result instanceof Object) {
            const userData = val?.result;
            localStorage.setItem("user", JSON.stringify(userData));

            // Get the dynamic home page path
            const homePath = getHomePage();
            // Ensure it starts with '/' and add hash
            const formattedPath = homePath.startsWith("/")
              ? homePath
              : `/${homePath}`;

            // Redirect to the correct URL format
            try {
              history.push(formattedPath);
            } catch (error) {
            }
            window.location.reload();
          } else {
            setMessage(
              val?.msg ?? "email or password is incorrect.please check"
            );
          }
        })
        .catch((val) => {
          setMessage(val ?? "An error occurred. Please try again later.");
        });
    } catch (error) {
      setMessage("An error occurred. Please try again later.");
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.keyCode === 13) {
        handleSubmit(onSubmit)();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleSubmit, onSubmit]);

  const handleTenantChange = (e) => {
    setSelectedTenantId(e.target.value);
  };

  const handleBack = () => history.push("/forgot-password");

  return (
    <Box className="login-wrapper">
      <Card className="card-wrapper">
        <div className="card-inner inner-logo">
          <img src={logo} style={{ width: "200px" }} alt="arise" />
          <h1 className="welcome-heading">Academic Portal</h1>
        </div>
        <div className="card-inner inner-form">
          <FormControl className={classes.formControl}>
            <InputLabel>Institute</InputLabel>
            <Select
              value={selectedTenantId}
              label="Institute"
              onChange={handleTenantChange}
            >
              {tenantIds.map((tenantId: any) => {
                return (
                  <MenuItem value={tenantId.tenantId}>
                    {tenantId.tenantName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl className={classes.formControl}>
            <TextField
              variant="outlined"
              type="email"
              name="email"
              label="Email"
              inputRef={register}
              error={!!errors?.email}
              helperText={errors?.email?.message}
            />
          </FormControl>
          <FormControl className={classes.formControl}>
            <TextField
              variant="outlined"
              type={showPassword ? "text" : "password"}
              name="password"
              label="Password"
              inputRef={register}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handlePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </FormControl>
          <Box
            display="flex"
            justifyContent="flex-end"
            className={classes.errorMessage}
          >
            {message && <FormHelperText>{message}</FormHelperText>}
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={handleSubmit(onSubmit)}
          >
            Submit
          </Button>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Typography onClick={handleBack} className={classes.back}>
              Forgot Password
            </Typography>
          </Box>
        </div>
      </Card>
    </Box>
  );
};

export default Login;
